import React, {useState} from 'react';
import {Card, Checkbox, Input, Modal, Radio, Space, Table, Tag} from 'antd';
import {useBoolean, useMount} from 'react-use';
import {UserApi, UserDto, UserRole} from '../../scaffold';
import {useOpenApiFpRequest} from '../../Http/useOpenApiRequest';
import CommonApiErrorHandler from '../../utils/HttpInstance';
import ImportUserExcel from '../../components/user/ImportExcel';
import Text from 'antd/lib/typography/Text';
import Flex from '../../components/Flex';
import UpdatePasswordForm from '../../components/user/UpdatePassword';
import UpdateStudentInfoBtn from '../../components/user/updateStudentInfoBtn';

const {confirm} = Modal;

export default function ManageUserPage() {
  const [role, setRole] = useState<UserRole>(UserRole.教师);
  const [keyword, setKeyword] = useState('');
  const [teacherType, setTeacherType] = useState<'导师' | '督导' | '盲审' | '论文初审'>('导师');
  const listState = useOpenApiFpRequest(UserApi, UserApi.prototype.userListGet);
  const toggleTeacherTypeState = useOpenApiFpRequest(UserApi, UserApi.prototype.userToggleTeacherTypePost);
  const removeState = useOpenApiFpRequest(UserApi, UserApi.prototype.userDeletePost);
  const [userId, setUserId] = useState<number>();
  function refresh() {
    listState.requestSync({});
  }
  useMount(() => {
    refresh();
  });
  const displayRows =
    listState.data
      ?.filter(i => i.role === role)
      .filter(i => {
        if (role === UserRole.教师) {
          if (teacherType === '督导' && !i.isSupervisor) return false;
          if (teacherType === '盲审' && !i.isJudge) return false;
          if (teacherType === '论文初审' && !i.isChecker) return false;
          // if (teacherType === '导师' && (i.isJudge || i.isSupervisor)) return false;
        }
        return true;
      })
      .filter(i => {
        if (!keyword) return true;
        return i.name?.includes(keyword);
      }) || [];

  function toggleIsJudge(user: UserDto) {
    toggleTeacherTypeState
      .request({
        userToggleTeacherTypeModel: {
          id: user.id || 0,
          isJudge: !user.isJudge,
          isSupervisor: !!user.isSupervisor,
          isChecker: !!user.isChecker,
        },
      })
      .then(refresh)
      .catch(CommonApiErrorHandler);
  }

  function toggleIsSupervisor(user: UserDto) {
    toggleTeacherTypeState
      .request({
        userToggleTeacherTypeModel: {
          id: user.id || 0,
          isJudge: !!user.isJudge,
          isChecker: !!user.isChecker,
          isSupervisor: !user.isSupervisor,
        },
      })
      .then(refresh)
      .catch(CommonApiErrorHandler);
  }

  function toggleIsChecker(user: UserDto) {
    toggleTeacherTypeState
      .request({
        userToggleTeacherTypeModel: {
          id: user.id || 0,
          isJudge: !!user.isJudge,
          isChecker: !user.isChecker,
          isSupervisor: !!user.isSupervisor,
        },
      })
      .then(refresh)
      .catch(CommonApiErrorHandler);
  }

  function remove(user: UserDto) {
    removeState
      .request({
        userFindParams: {
          id: user.id || 0,
        },
      })
      .then(refresh)
      .catch(CommonApiErrorHandler);
  }

  return (
    <Card title={'用户管理'}>
      <Flex className={'m-b-10'} justify={'space-between'} align={'center'}>
        <Space className={'m-b-10'}>
          <Radio.Group value={role} onChange={e => setRole(e.target.value)}>
            <Radio.Button value={UserRole.学生}>学生</Radio.Button>
            <Radio.Button value={UserRole.教师}>教师</Radio.Button>
          </Radio.Group>
          <Input value={keyword} onChange={e => setKeyword(e.target.value)} placeholder={'输入关键词检索'} />
          {role === UserRole.教师 && (
            <Radio.Group value={teacherType} onChange={e => setTeacherType(e.target.value)}>
              <Radio value={'导师'}>导师</Radio>
              <Radio value={'督导'}>督导</Radio>
              <Radio value={'论文初审'}>论文初审</Radio>
              <Radio value={'盲审'}>盲审</Radio>
            </Radio.Group>
          )}
        </Space>
        <ImportUserExcel refresh={refresh} role={role} teacherType={teacherType} />
      </Flex>
      <Table rowKey={'id'} dataSource={displayRows} size={'small'} bordered loading={listState.loading}>
        <Table.Column<UserDto>
          title={'姓名'}
          dataIndex={'name'}
          render={(_, item) => (
            <div>
              <Text className={'m-r-5'} strong>
                {item.name}
              </Text>
              {item.isSupervisor && <Tag color={'red'}>督</Tag>}
              {item.isJudge && <Tag color={'purple'}>盲</Tag>}
              {item.isChecker && <Tag color={'blue'}>格</Tag>}
            </div>
          )}
        />
        <Table.Column<UserDto> title={role === UserRole.教师 ? '工号' : '学号'} dataIndex={'username'} />
        {role === UserRole.学生 && <Table.Column<UserDto> title={'导师'} render={(_, item) => <Text>{item.teacher?.name}</Text>} />}
        {role === UserRole.学生 && <Table.Column<UserDto> title={'专业'} render={(_, item) => <Text>{item.school}</Text>} />}
        {role === UserRole.学生 && <Table.Column<UserDto> title={'入学年份'} render={(_, item) => <Text>{item.grade}</Text>} />}
        {role === UserRole.教师 && <Table.Column<UserDto> title={'学校'} render={(_, item) => <Text>{item.school}</Text>} />}
        <Table.Column<UserDto> title={'联系方式'} dataIndex={'telephone'} />
        <Table.Column<UserDto> title={'邮箱'} dataIndex={'email'} />
        <Table.Column<UserDto>
          title={'操作'}
          render={(_, item) => (
            <Space>
              {item.role === UserRole.教师 && <a onClick={() => toggleIsSupervisor(item)}>{item.isSupervisor ? '撤销督导' : '设为督导'}</a>}
              {item.role === UserRole.教师 && <a onClick={() => toggleIsJudge(item)}>{item.isJudge ? '撤销专业盲审' : '设为专业盲审'}</a>}
              {item.role === UserRole.教师 && <a onClick={() => toggleIsChecker(item)}>{item.isChecker ? '撤销论文初审' : '设为论文初审'}</a>}
              {item.role === UserRole.学生 && <UpdateStudentInfoBtn userId={item.id as number} refresh={refresh} />}
              <a onClick={() => setUserId(item.id)}>修改密码</a>
              <a onClick={() => remove(item)}>删除用户</a>
            </Space>
          )}
        />
      </Table>
      <Modal title={'修改密码'} visible={!!userId} onCancel={() => setUserId(undefined)} footer={false}>
        {userId && (
          <UpdatePasswordForm
            userId={userId}
            onSuccess={() => {
              setUserId(undefined);
            }}
          />
        )}
      </Modal>
    </Card>
  );
}
